import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function formatDate(dateValue) {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function SignedCheckinsDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSignedCheckins();
    }
  }, [open]);

  const fetchSignedCheckins = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/signed-checkins`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('No se pudieron cargar los check-ins firmados');
      }
      const data = await response.json();
      setRecords(data);
    } catch (err) {
      console.error('Error cargando check-ins firmados:', err);
      setError(err.message || 'Error al cargar los check-ins firmados');
    } finally {
      setLoading(false);
    }
  };

  const downloadSignedCheckin = async (bookingId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/checkin/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'No se pudo descargar el check-in firmado');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CheckinFirmado_${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error descargando check-in firmado:', err);
      setError(err.message || 'Error al descargar el check-in firmado');
    }
  };

  const deleteSignedCheckin = async (bookingId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/checkin`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'No se pudo eliminar el check-in firmado');
      }
      setDeleteConfirm(null);
      await fetchSignedCheckins();
    } catch (err) {
      console.error('Error eliminando check-in firmado:', err);
      setError(err.message || 'Error al eliminar el check-in firmado');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Check-ins firmados</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID Reserva</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Habitación</TableCell>
                  <TableCell>Fechas</TableCell>
                  <TableCell>Firmado</TableCell>
                  <TableCell>Por</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No hay check-ins firmados aún.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow key={record._id} hover>
                      <TableCell>{record.bookingId}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {record.guestInfo?.firstName} {record.guestInfo?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.guestInfo?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.roomName}</TableCell>
                      <TableCell>
                        {formatDate(record.checkIn)} → {formatDate(record.checkOut)}
                      </TableCell>
                      <TableCell>{formatDate(record.checkinSignedAt)}</TableCell>
                      <TableCell>{record.checkinSignedBy || '-'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Descargar PDF firmado">
                            <IconButton size="small" onClick={() => downloadSignedCheckin(record.bookingId)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar check-in">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(record.bookingId)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>¿Eliminar check-in firmado?</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar este check-in firmado? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={() => deleteSignedCheckin(deleteConfirm)}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
